import React, { useState } from "react";
import { Card, Button, Table, Modal, Alert, Spinner } from "react-bootstrap";
import { IconPlus, IconEdit, IconTrash, IconEye } from "@tabler/icons-react";
import { useATVStatus } from "../context/ATVStatusContext";
import ATVStatusForm from "./ATVStatusForm";

const ATVStatusCRUD = ({ show, onHide }) => {
  const { atvStatuses, filterOptions, handleSearch, refetch, Pagination } =
    useATVStatus();

  const [showModal, setShowModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingStatus, setViewingStatus] = useState(null);

  // Handle create new status
  const handleCreate = () => {
    setEditingStatus(null);
    setShowModal(true);
  };

  // Handle edit status
  const handleEdit = (status) => {
    setEditingStatus(status);
    setShowModal(true);
  };

  // Handle view status details
  const handleView = (status) => {
    setViewingStatus(status);
    setShowViewModal(true);
  };

  // Handle delete status
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this status?")) {
      return;
    }

    try {
      const response = await fetch(`/api/atv-status/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      refetch(); // Refresh the data
    } catch (err) {
      console.error("Error deleting status:", err);
      alert("Failed to delete status. Please try again.");
    }
  };

  // Handle form success
  const handleFormSuccess = () => {
    setShowModal(false);
    refetch(); // Refresh the data
  };

  // Handle form error
  const handleFormError = (error) => {
    console.error("Form error:", error);
    alert("Failed to save status. Please try again.");
  };

  // Close modals
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingStatus(null);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewingStatus(null);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" className="workshop-modal-scope">
      <Modal.Header closeButton>
        <Modal.Title>ATV Status Management</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          style={{
            minHeight: "60vh",
            backgroundColor: "#f9fafb",
            padding: "16px",
          }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <h2 style={{ fontSize: "20px", fontWeight: "600" }}>
                  ATV Statuses
                </h2>
                <Button
                  variant="primary"
                  onClick={handleCreate}
                  disabled={atvStatuses.loading}
                >
                  <IconPlus size={16} className="me-2" />
                  Add Status
                </Button>
              </div>

              {/* Search */}
              <div className="mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search statuses..."
                  onKeyPress={handleSearch}
                  defaultValue={filterOptions.search}
                />
              </div>

              {atvStatuses.error && (
                <Alert variant="danger" className="mb-3">
                  {atvStatuses.error}
                </Alert>
              )}

              <Card>
                <div style={{ padding: "0" }}>
                  {atvStatuses.loading ? (
                    <div className="text-center p-4">
                      <Spinner animation="border" />
                      <p className="mt-2">Loading statuses...</p>
                    </div>
                  ) : (
                    <Table striped bordered hover style={{ marginBottom: "0" }}>
                      <thead>
                        <tr>
                          <th>Status Name</th>
                          <th style={{ textAlign: "center", width: "150px" }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {atvStatuses.data.length === 0 ? (
                          <tr>
                            <td colSpan="2" className="text-center py-4">
                              No statuses found. Click "Add Status" to create
                              your first status.
                            </td>
                          </tr>
                        ) : (
                          atvStatuses.data.map((status, index) => (
                            <tr key={status.id || index}>
                              <td>{status.name}</td>
                              <td style={{ textAlign: "center" }}>
                                <Button
                                  variant="outline-info"
                                  className="m-1"
                                  onClick={() => handleView(status)}
                                >
                                  <IconEye size={14} />
                                </Button>
                                <Button
                                  variant="outline-primary"
                                  className="m-1"
                                  onClick={() => handleEdit(status)}
                                >
                                  <IconEdit size={14} />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  onClick={() => handleDelete(status.id)}
                                  className="m-1"
                                >
                                  <IconTrash size={14} />
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
              </Card>

              {/* Pagination */}
              {atvStatuses.data.length > 0 && (
                <div className="d-flex justify-content-center mt-3">
                  {Pagination}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Create/Edit Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg" className="workshop-modal-scope">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingStatus ? "Edit ATV Status" : "Add New ATV Status"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ATVStatusForm
              initialData={editingStatus}
              onSuccess={handleFormSuccess}
              onError={handleFormError}
            />
          </Modal.Body>
        </Modal>

        {/* View Modal */}
        <Modal show={showViewModal} onHide={handleCloseViewModal} size="lg" className="workshop-modal-scope">
          <Modal.Header closeButton>
            <Modal.Title>ATV Status Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {viewingStatus && (
              <div>
                <div className="row mb-3">
                  <div className="col-md-12">
                    <strong>Status Name:</strong>
                    <p>{viewingStatus.name}</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <strong>Created At:</strong>
                    <p>
                      {viewingStatus.created_at
                        ? new Date(viewingStatus.created_at).toLocaleString()
                        : "N/A"}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <strong>Updated At:</strong>
                    <p>
                      {viewingStatus.updated_at
                        ? new Date(viewingStatus.updated_at).toLocaleString()
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
        </Modal>
      </Modal.Body>
    </Modal>
  );
};

export default ATVStatusCRUD;
